import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Crown, Check, X, Sparkles, Brain, Zap, Loader2, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { label: 'AI Courses — video lessons + 15-day crash course', free: false, pro: true, highlight: true },
  { label: '97+ Percentile Report — prediction + AI action plan', free: false, pro: true, highlight: true },
  { label: 'Monthly bonus points (quiz pool entry)', free: '0', pro: '500 / month auto-refill', highlight: true },
  { label: 'AI practice quizzes per day', free: '5', pro: 'Unlimited', highlight: true },
  { label: '⭐ Super Points — top 3% leaderboard', free: false, pro: 'up to 15,000 / month' },
  { label: '7-day streak bonus (30q/day)', free: '+50 pts', pro: '+50 pts' },
  { label: 'Live quiz pools', free: '✓', pro: '✓' },
  { label: 'AI weakness reports (Claude Opus)', free: false, pro: true },
  { label: 'Lifetime analytics + topic heatmap', free: '7-day chart', pro: '✓ Lifetime' },
  { label: 'Reward redemption', free: '✓', pro: '✓ + 10% bonus pts' },
];

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Upgrade = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [planType, setPlanType] = useState('annual'); // default to annual — best value & LTV
  const [startingTrial, setStartingTrial] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          axios.get(`${API_URL}/api/subscriptions/status`),
          axios.get(`${API_URL}/api/subscriptions/plan`),
        ]);
        setStatus(s.data);
        setPlan(p.data);
      } catch (err) {
        toast.error('Failed to load subscription details');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleUpgrade = async () => {
    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error('Razorpay checkout failed to load. Try again.');
        setPaying(false);
        return;
      }
      const { data } = await axios.post(`${API_URL}/api/subscriptions/create`, { plan: planType });
      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: 'Aptivon Pro',
        description: planType === 'annual' ? 'Annual subscription — ₹2,499 / year' : 'Monthly subscription — ₹299 / month',
        prefill: {
          name: data.user.name,
          email: data.user.email,
        },
        theme: { color: '#EF4444' },
        handler: async (response) => {
          try {
            const verify = await axios.post(`${API_URL}/api/subscriptions/verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            });
            const bonus = verify.data?.bonus_points || 500;
            toast.success(`🎉 Welcome to Aptivon Pro! +${bonus} points credited.`);
            await refreshUser();
            navigate('/dashboard');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        toast.error('Payment failed. Please try again.');
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setPaying(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your Pro subscription? You will keep Pro access until the end of the current billing period.')) return;
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscriptions/cancel`);
      toast.success(data.message);
      await refreshUser();
      const s = await axios.get(`${API_URL}/api/subscriptions/status`);
      setStatus(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscriptions/trial/start`);
      toast.success(data.message || 'Your 7-day Pro trial is live!');
      await refreshUser();
      const s = await axios.get(`${API_URL}/api/subscriptions/status`);
      setStatus(s.data);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-10 h-10 text-[#0EA5E9] animate-spin" />
        </div>
      </div>
    );
  }

  const isPro = status?.is_pro;

  return (
    <div className="min-h-screen" data-testid="upgrade-page">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4">
            <Crown className="w-4 h-4" /> Aptivon Pro
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#111827] mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
            data-testid="upgrade-title"
          >
            Unlock your <span className="gradient-text">All-India rank</span>
          </h1>
          <p className="text-base sm:text-lg text-[#6B7280] max-w-2xl mx-auto">
            Pro members crack JEE/NEET 3× faster — unlimited AI quizzes, personalised weakness reports, and lifetime analytics.
          </p>
        </div>

        {/* 7-day Free Trial CTA */}
        {!isPro && status?.trial_eligible && (
          <div className="relative overflow-hidden rounded-3xl p-6 sm:p-7 mb-8 bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-xl" data-testid="trial-cta-card">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/20 rounded-full blur-3xl" />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-xs font-bold uppercase tracking-wider mb-3">
                  <Sparkles className="w-3.5 h-3.5" /> Limited offer
                </div>
                <h2 className="text-2xl sm:text-3xl font-black mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Try Pro free for 7 days
                </h2>
                <p className="text-sm sm:text-base opacity-95">
                  Unlimited AI quizzes · Weakness reports · +500 bonus points credited instantly. No card. No catch.
                </p>
              </div>
              <button
                onClick={handleStartTrial}
                disabled={startingTrial}
                className="shrink-0 w-full sm:w-auto px-6 py-4 rounded-2xl font-black bg-white text-amber-600 shadow-lg hover:shadow-2xl transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="start-trial-button"
              >
                {startingTrial ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                {startingTrial ? 'Activating…' : 'Start free trial'}
              </button>
            </div>
          </div>
        )}

        {/* Free quota indicator */}
        {!isPro && status?.free_quota && (
          <div className="mb-8 max-w-md mx-auto p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200" data-testid="free-quota-banner">
            <div className="flex items-center justify-between mb-2 text-sm font-bold text-amber-800">
              <span>Today's free quizzes</span>
              <span>{status.free_quota.used} / {status.free_quota.limit} used</span>
            </div>
            <div className="h-2 rounded-full bg-amber-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all"
                style={{ width: `${Math.min(100, (status.free_quota.used / status.free_quota.limit) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Monthly / Annual toggle */}
        {!isPro && (
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="plan-type-toggle">
              <button
                onClick={() => setPlanType('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-bold transition ${planType === 'monthly' ? 'bg-white text-fg shadow' : 'text-fg-muted'}`}
                style={planType === 'monthly' ? { color: '#DC2626' } : {}}
                data-testid="plan-toggle-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setPlanType('annual')}
                className={`relative px-5 py-2 rounded-full text-sm font-bold transition ${planType === 'annual' ? 'bg-white shadow' : 'text-fg-muted'}`}
                style={planType === 'annual' ? { color: '#DC2626' } : {}}
                data-testid="plan-toggle-annual"
              >
                Annual
                <span className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-400 text-white">SAVE 30%</span>
              </button>
            </div>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Free */}
          <div className="rounded-3xl p-7 bg-white border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Free Forever</p>
            <p className="text-4xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              ₹0
              <span className="text-base font-medium text-[#9CA3AF]"> / month</span>
            </p>
            <p className="text-sm text-[#6B7280] mt-1 mb-6">Get a feel for the platform.</p>
            <ul className="space-y-2.5">
              {FEATURES.map((f) => (
                <li key={`free-${f.label}`} className="flex items-start gap-2 text-sm">
                  {f.free === false ? (
                    <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  ) : (
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  )}
                  <span className={f.free === false ? 'text-[#9CA3AF] line-through' : 'text-[#374151]'}>
                    {f.label}{typeof f.free === 'string' ? `: ${f.free}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className="relative rounded-3xl p-7 text-white shadow-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #DC2626 0%, #7F1D1D 100%)' }}>
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-amber-400/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
            <div className="relative">
              <span className="absolute -top-3 right-0 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-[#111827] shadow-md">
                Most Popular
              </span>
              <p className="text-xs font-bold uppercase tracking-wider opacity-80 mb-2">Aptivon Pro</p>
              <p className="text-4xl font-black" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="pro-price">
                ₹{planType === 'annual' ? (plan?.annual_amount_inr || 2499).toLocaleString() : (plan?.amount_inr || 299)}
                <span className="text-base font-medium opacity-75">{planType === 'annual' ? ' / year' : ' / month'}</span>
              </p>
              <p className="text-sm opacity-90 mt-1 mb-2">
                {planType === 'annual'
                  ? `Effective ₹${Math.round((plan?.annual_amount_inr || 2499) / 12)}/mo · Saves ${plan?.annual_savings_percent || 30}% vs monthly`
                  : 'Cancel anytime. No hidden fees.'}
              </p>
              {planType === 'annual' && (
                <p className="inline-flex items-center gap-1.5 text-xs font-bold mb-2 px-3 py-1.5 bg-amber-400/25 border border-amber-300/40 rounded-full" data-testid="per-day-framing">
                  ☕ Less than ₹{Math.ceil((plan?.annual_amount_inr || 2499) / 365)}/day — cheaper than a cup of chai
                </p>
              )}
              <p className="text-xs opacity-90 mb-5 px-3 py-2 bg-white/15 rounded-xl border border-white/20" data-testid="monthly-points-info">
                💎 +{plan?.monthly_points_refill || 500} points refilled every month
              </p>

              <ul className="space-y-2.5 mb-7">
                {FEATURES.map((f) => (
                  <li key={`pro-${f.label}`} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                    <span className={f.highlight ? 'font-bold' : ''}>
                      {f.label}{typeof f.pro === 'string' ? `: ${f.pro}` : ''}
                    </span>
                  </li>
                ))}
              </ul>

              {isPro ? (
                <div className="space-y-3" data-testid="pro-active-block">
                  <div className="flex items-center gap-2 px-4 py-3 bg-white/15 backdrop-blur-sm rounded-2xl border border-white/20">
                    <Crown className="w-5 h-5 text-amber-300" />
                    <span className="font-bold text-sm">You're a Pro Member</span>
                  </div>
                  {status.current_period_end && (
                    <p className="text-xs opacity-80">
                      Renews on {new Date(status.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                  <button
                    onClick={handleCancel}
                    disabled={cancelling || status.subscription_status === 'cancelled'}
                    className="w-full px-6 py-3 rounded-2xl font-bold bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/20 transition disabled:opacity-50 text-sm"
                    data-testid="cancel-subscription-button"
                  >
                    {cancelling ? 'Cancelling…' : status.subscription_status === 'cancelled' ? 'Cancellation scheduled' : 'Cancel subscription'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleUpgrade}
                  disabled={paying}
                  className="w-full px-6 py-4 bg-amber-400 hover:bg-amber-300 text-[#111827] rounded-2xl font-black shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none transition-all text-base flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="subscribe-button"
                >
                  {paying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Opening checkout…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" /> {planType === 'annual' ? `Get Pro Annual · ₹${(plan?.annual_amount_inr || 2499).toLocaleString()}` : 'Get Pro for ₹299/mo'}
                    </>
                  )}
                </button>
              )}
              <p className="mt-3 text-xs opacity-75 text-center">
                Powered by Razorpay · Secure UPI / Cards / NetBanking
              </p>
            </div>
          </div>
        </div>

        {/* Why pro */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {[
            { Icon: Zap, title: 'Unlimited practice', desc: 'Burn through 50+ AI quizzes a day. No daily caps.' },
            { Icon: Brain, title: 'AI weakness reports', desc: 'Claude Opus analyses every wrong answer and tells you exactly what to revise.' },
            { Icon: Crown, title: '10% bonus rewards', desc: 'Every gift card redemption gets 10% extra value as a Pro member.' },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                <b.Icon className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="font-bold text-[#111827] mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>{b.title}</h3>
              <p className="text-sm text-[#6B7280]">{b.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm font-bold text-[#6B7280] hover:text-[#0EA5E9] transition" data-testid="upgrade-back-link">
            Back to dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Upgrade;
